import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

// Phase 17b — trade API helpers.

export function useGenerateTradeQR() {
  return useMutation({
    mutationFn: async ({ target_user_id }) => {
      const { data } = await api.post("/trade/generate-qr", { target_user_id });
      return data;
    },
  });
}

export function useScanTradeQR() {
  return useMutation({
    mutationFn: async (payload) => {
      // payload is either {token} or {pin}
      const { data } = await api.post("/trade/scan-qr", payload);
      return data;
    },
  });
}

export function useTradeSession(sessionId, { enabled = true, refetchInterval = 1500 } = {}) {
  return useQuery({
    queryKey: ["trade-session", sessionId],
    queryFn: async () => {
      const { data } = await api.get(`/trade/session/${sessionId}`);
      return data;
    },
    enabled: enabled && !!sessionId,
    refetchInterval,
    // Keep polling silent — never surface a UI toast from a poll error.
    retry: false,
  });
}

export function useCancelTradeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ session_id }) => {
      const { data } = await api.post(`/trade/session/${session_id}/cancel`);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["trade-session", data.session_id], data);
    },
  });
}

// Utility: format a 6-digit PIN as `123-456` for display.
export function formatPin(pin) {
  if (!pin || pin.length !== 6) return pin || "";
  return pin.slice(0, 3) + "-" + pin.slice(3);
}

// Utility: strip non-digits from user input so the backend accepts hyphens/spaces.
export function normalisePin(input) {
  return String(input || "").replace(/\D/g, "");
}

// Extract a specific error detail from an axios error, falling back to
// a generic key. Used to map backend `detail` strings to `t()` keys.
export function tradeErrorKey(err) {
  const detail = err?.response?.data?.detail;
  const knownCodes = new Set([
    "token_expired",
    "token_invalid",
    "token_wrong_kind",
    "pin_not_found_or_expired",
    "not_the_intended_target",
    "already_matched",
    "cannot_scan_own_qr",
    "target_not_in_radar",
    "no_mutual_trade",
  ]);
  if (typeof detail === "string" && knownCodes.has(detail)) return detail;
  if (
    detail &&
    typeof detail === "object" &&
    detail.detail === "no_mutual_trade"
  ) {
    return "no_mutual_trade";
  }
  return "generic";
}

// Not used by API but exposed for potential visualisers.
export const TRADE_TTL_SECONDS = 300;

// Small explicit re-export so components can drop `axios` too if they want.
export { axios };
